import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiInstagram, FiFacebook, FiYoutube, FiMapPin, FiPhone, FiMail, FiMessageCircle, FiArrowUpRight, FiChevronDown } from 'react-icons/fi';
import logo from '../assets/logo-script.png';
import forest from '../assets/sustainability-forest.jpg';
import TamarindLeaf from './brand/TamarindLeaf';

const QUICK = [
  ['About Us', '/about'],
  ['Shop All', '/shop'],
  ['Fabrics to Silhouette', '/fabrics-to-silhouette'],
  ['Dresses', '/shop?productType=dress'],
  ['Jewellery', '/shop?productType=jewellery'],
  ['New Arrivals', '/shop?newArrivals=true'],
  ['Sale', '/shop?onSale=true'],
];

const SERVICE = [
  ['My Account', '/profile'],
  ['Track Order', '/orders'],
  ['Shipping Policy', '/shipping-policy'],
  ['Return Policy', '/return-policy'],
  ['FAQs', '/faq'],
];

const CONTACTS = [
  { Icon: FiMapPin, label: 'Visit the studio', value: '118, First Floor, Wadala East, Antop Hill, Mumbai, Maharashtra 400037', href: 'https://maps.google.com/?q=Wadala+East+Antop+Hill+Mumbai+400037' },
  { Icon: FiPhone, label: 'Call us', value: '+91 98199 29937', href: 'tel:+919819929937' },
  { Icon: FiMail, label: 'Email us', value: 'thetamarindleaf@gmail.com', href: 'mailto:thetamarindleaf@gmail.com' },
];

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(min-width: 901px)').matches
  ));

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(min-width: 901px)');
    const sync = (event) => setIsDesktop(event.matches);
    setIsDesktop(media.matches);
    if (media.addEventListener) media.addEventListener('change', sync);
    else media.addListener(sync);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', sync);
      else media.removeListener(sync);
    };
  }, []);

  return isDesktop;
};

const FooterSection = ({ title, isDesktop, open, onToggle, children }) => (
  <details className="ft__section" open={isDesktop || open}>
    <summary
      className="ft__summary"
      onClick={(event) => {
        event.preventDefault();
        if (!isDesktop) onToggle();
      }}
    >
      <span className="ft__heading">{title}</span>
      <FiChevronDown className="ft__chevron" aria-hidden="true" />
    </summary>
    {children}
  </details>
);

const Footer = ({ showCta = false }) => {
  const isDesktop = useIsDesktop();
  const [openSections, setOpenSections] = useState({});
  const toggle = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
  <>
    {showCta && (
      <section className="cta" style={{ backgroundImage: `url(${forest})` }}>
        <div className="cta__veil" />
        <div className="cta__card">
          <div className="cta__head">
            <TamarindLeaf filled vertical className="cta__leaf" />
            <h2>Made By Hand,<br />Made For You</h2>
            <span className="cta__rule" />
          </div>
          <div className="cta__body">
            <div className="cta__copy">
              <p>Every piece begins as a thread, a colour, a choice: yours to make. Tell us the fit, the fabric, the finish, and our artisans shape it by hand, just for you. Nothing here is mass-made. Nothing here is generic.</p>
              <Link to="/fabrics-to-silhouette" className="cta__link">Start your custom piece</Link>
              <p className="cta__sign">— The Tamarind Leaf</p>
            </div>
            <ul className="cta__contacts">
              {CONTACTS.map(({ Icon, label, value, href }) => (
                <li key={label}>
                  <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">
                    <span className="cta__icon"><Icon /></span>
                    <span className="cta__text"><small>{label}</small><b>{value}</b></span>
                    <FiArrowUpRight className="cta__go" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    )}

    <footer className="ft">
      <div className="ft__grid">
        <div className="ft__brand">
          <img src={logo} alt="The Tamarind Leaf" className="ft__logo" />
          <p>Your destination for elegant women's fashion. We bring you the finest dresses and exquisite jewellery, crafted with love and attention to detail.</p>
          <div className="ft__social">
            <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><FiInstagram /></a>
            <a href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FiFacebook /></a>
            <a href="https://www.youtube.com/" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><FiYoutube /></a>
          </div>
        </div>

        <FooterSection
          title="Quick Links"
          isDesktop={isDesktop}
          open={Boolean(openSections.quick)}
          onToggle={() => toggle('quick')}
        >
          <nav aria-label="Quick links">
            <ul>{QUICK.map(([label, to]) => <li key={label}><Link to={to}>{label}</Link></li>)}</ul>
          </nav>
        </FooterSection>

        <FooterSection
          title="Customer Service"
          isDesktop={isDesktop}
          open={Boolean(openSections.service)}
          onToggle={() => toggle('service')}
        >
          <nav aria-label="Customer service">
            <ul>{SERVICE.map(([label, to]) => <li key={label}><Link to={to}>{label}</Link></li>)}</ul>
          </nav>
        </FooterSection>

        <FooterSection
          title="Contact Us"
          isDesktop={isDesktop}
          open={Boolean(openSections.contact)}
          onToggle={() => toggle('contact')}
        >
          <ul className="ft__contact">
            <li><FiMapPin /><span>118, First Floor, Wadala East, Wadala Truck Terminal Rd, Antop Hill, Mumbai, Maharashtra - 400037</span></li>
            <li><FiPhone /><a href="tel:+919819929937">9819929937</a></li>
            <li><FiMessageCircle /><a href="https://wa.me/919819929937" target="_blank" rel="noopener noreferrer">WhatsApp: +91 98199 29937</a></li>
            <li><FiMail /><a href="mailto:thetamarindleaf@gmail.com">thetamarindleaf@gmail.com</a></li>
          </ul>
        </FooterSection>
      </div>

      <div className="ft__bar">
        <span>© {new Date().getFullYear()} The Tamarind Leaf. All rights reserved.</span>
        <span className="ft__legal">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
        </span>
      </div>
    </footer>
  </>
  );
};

export default Footer;
